import Vue from 'vue'
import SEOMixin from "@/mixins/SEO";
import { parseMinPrice, slugify } from '@/helpers/helpers'
import { ROLES } from '@/utils/userRoles'
import { isExportCustomer, isEuropeDealar } from '@/utils/userFolder'

const mixin = {
  mixins: [SEOMixin],

  computed: {
    __isLoggedIn() {
      return this.$auth.loggedIn
    },

    __isDealerAdmin() {
      return this.$auth.hasScope(ROLES.DEALER_ADMIN)
    },

    __isDealerUser() {
      return this.$auth.hasScope(ROLES.DEALER_USER)
    },

    __isMSRP() {
      return !!this.$auth.user?.isMsrpPrice && !isExportCustomer(this.$auth.user?.userFolder)
    },

    __isYourPrice() {
      return this.__isDealerAdmin && !!this.$auth.user?.isYourPrice
    },

    __getCurrencySign() {
      return (this.__isLoggedIn && isEuropeDealar(this.$auth.user?.userFolder)) ? '€' : '$'
    },

    __isEuropeanUser() {
      return !!this.$auth.user?.european
    },

    __isExportUser() {
      return !!this.$auth.user?.export
    },

    // CND / USA Online Only (EBMS customer folders 27, 29): Canadian Timber only.
    __isOnlineOnlyUser() {
      return !!this.$auth.user?.onlineOnly
    },

    __labelMsrp() {
      if (this.__isEuropeanUser) return 'Retail Price'
      if (this.__isExportUser) return 'Dealer Price'
      return 'MSRP'
    },

    __labelYourPrice() {
      if (this.__isEuropeanUser) return 'Dealer Price'
      if (this.__isExportUser) return 'Extra Discount'
      return 'Your Price'
    },

    __labelMsrpTotal() {
      if (this.__isEuropeanUser) return 'Retail Price Total'
      if (this.__isExportUser) return 'Dealer Price Total'
      return 'MSRP Total'
    }
  },

  methods: {
    __currency(value) {
      if (value === null || value === undefined || value === '') return '-'

      const str = String(value).trim()

      const fmt = (n) => {
        const num = Number(n)
        if (!isFinite(num)) return '-'
        const sign = num < 0 ? '-' : ''
        // Always two decimals, rounded half-up on the decimal value: toFixed rounds
        // the binary approximation instead ((35.395).toFixed(2) is "35.39"), so a
        // price could show a cent below the same figure in EBMS and on the invoice.
        const abs = (Math.round(Number((Math.abs(num) * 100).toPrecision(12))) / 100).toFixed(2)
        return `${sign}${this.__getCurrencySign} ${abs.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`
      }

      const isRange = /^\s*-?\d+(?:\.\d+)?\s*-\s*-?\d+(?:\.\d+)?\s*$/.test(str)
      if (isRange) {
        const [a, b] = str.split(/\s*-\s*/)
        return `${fmt(a)} - ${fmt(b)}`
      }

      return fmt(str)
    },

    async __logout() {
      try {
        await this.$auth.logout()

        this.$store.dispatch("CART/GET");
      } catch (err) {
        this.__logError(err)
      }
    },

    getProductLink(product) {
      if (product.superLink) {
        const ID = encodeURIComponent(product.superLink.id)
        const SLUG = slugify(product.superLink.name)
        return `/product/${ID}/${SLUG}`
      }

      // An item without a slug falls back to its id (else the link is /undefined);
      // _slug.vue still resolves a raw product id through /product.
      const SLUG = product.slug || encodeURIComponent(product.id)

      if (process.client) {
        window.__productSlugs = window.__productSlugs || new Set()
        window.__productSlugs.add(SLUG)
      }

      return `/${SLUG}`
    },

    getCategoryLink(category) {
      if (category.slug) return '/' + category.slug
      return '/catalog/' + category.treeId
    },

    __getDiscountPercent(price, oldPrice) {
      const p = parseMinPrice(price);
      const op = parseMinPrice(oldPrice);

      if (p == null || op == null || p <= 0 || op <= 0 || op <= p) {
        return "";
      }

      const percent = Math.round(((op - p) / op) * 100);

      if (percent > 0)
        return `-${percent}% off`;

      return ""
    },

    onImgLoaded(e) {
      const img = e.target

      img.classList.remove('img--loading')
      img.classList.add('img--loaded')
    },

    json(obj) {
      return JSON.stringify(obj, null, '\t')
    },

    __log(msg) {
      const styles =
        `background: #f2f0eb;
        color: #000;
        padding: 1px 6px;
        font-size: 10px;
        border-radius: 2px;`

      if (this.$config.IS_DEV) {
        console.log(`%capp`, styles, msg)
      }
    },

    __logError(error) {
      const styles =
        `background: #e3170a;
        color: #fff;
        padding: 1px 6px;
        font-size: 10px;
        border-radius: 2px;`

      console.log(`%cerror`, styles, error?.message || error)
    },

    __getArrowIcon() {
      return `<div class="icon--mask" data-icon="arrow"></div>`
    },

    _getActiveConfigurationItems(configurations) {
      return configurations
        .map(configuration => {
          return {
            configuration: configuration.name,
            ...configuration.items.find(configurationItem => configurationItem.active),
          }
        })
        .filter(configuration => configuration && configuration.id && configuration.id !== -1)
    },

    __toastError(message) {
      this.$toast.add({
        severity: "error",
        group: "general",
        life: 8000,
        message,
      });
    },

    __toast(message) {
      this.$toast.add({
        severity: "success",
        group: "general",
        life: 8000,
        message,
      });
    },

    __toTop() {
      if (typeof window === 'object') {
        window.scrollTo(0, 0);
      }
    },

    __resetForm(form) {
      Object.keys(form).forEach((key) => {
        form[key] = "";
      });
    },

    __getIsValidPrice(value) {
      const num = Number(value);

      return typeof value !== 'boolean' && Number.isFinite(num) && num > 0;
    },

    _getServerFormatedConfigurations(configurations) {
      return configurations
        .map(configuration => {
          const formated = {
            name: configuration.name,
          }

          const active = configuration.items.find(configurationItem => configurationItem.active)

          if (active) {
            formated.id = active.id
            formated.quan = active.quan
            formated.cType = active.cType
            formated.components = active.components

            if (active.nested?.configurations?.length) {
              formated.childs = this._getServerFormatedConfigurations(active.nested.configurations)
            }
          }

          return {
            ...formated
          }
        })
        .filter(configuration => configuration && configuration.id && configuration.id !== -1)
    },

    // The configuration tree as stored on a cart line (formatedConfigurations): the same
    // tree minus each item's slider instance, kept recursively through nested picks.
    _getCleanConfigurations(configurations) {
      return configurations.map(configuration => {
        return {
          ...configuration,
          items: configuration.items.map(item => {
            const { slider, nested, ...rest } = item

            if (nested?.configurations?.length) {
              return {
                ...rest,
                nested: {
                  ...nested,
                  configurations: this._getCleanConfigurations(nested.configurations),
                },
              }
            }

            return { ...rest, ...(nested ? { nested: { ...nested } } : {}) }
          }),
        }
      })
    },

    _getListOfActiveConfigurations(configurations) {
      let list = []

      const findActive = (configurations, level) => {
        configurations.forEach(configuration => {
          const active = configuration.items.find(configurationItem => configurationItem.active)

          if (active && active.id !== -1) {
            list.push({
              configuration: configuration.name,
              level,
              ...active
            })

            if (active.nested?.configurations?.length) {
              findActive(active.nested.configurations, level + 1)
            }
          }

        })
      }

      findActive(configurations, 0)

      return list
    },


    __scrollIntoView(el, offset = 0) {
      window.scrollTo({
        behavior: 'smooth',
        top:
          el.getBoundingClientRect().top -
          document.body.getBoundingClientRect().top -
          offset
      })
    },

    __getUniqKey() {
      return `${Math.random()}`
    },
  }
}

Vue.mixin(mixin)